const API_BASE_STORAGE_KEY = 'academic-review-api-base';
const AUTH_TOKEN_STORAGE_KEY = 'academic-review-auth-token';
const SUPABASE_SESSION_STORAGE_KEY = 'academic-review-supabase-session';
const SUPABASE_BASE_URL = typeof import.meta !== 'undefined' ? (import.meta.env.VITE_SUPABASE_URL as string | undefined) : undefined;
const SUPABASE_ANON_KEY = typeof import.meta !== 'undefined' ? (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) : undefined;
const ENV_API_BASE = typeof import.meta !== 'undefined' ? (import.meta.env.VITE_API_BASE_URL as string | undefined) : undefined;
const DEFAULT_PRODUCTION_API_BASE = 'https://academic-cluster-edge-api.zhongfeng1922.workers.dev';
type SupabaseClientType = import('@supabase/supabase-js').SupabaseClient;
type SupabaseSessionType = import('@supabase/supabase-js').Session;

let supabaseClientPromise: Promise<SupabaseClientType | null> | null = null;
let supabaseAuthListenerReady = false;

function normalizeApiBase(value: string | null | undefined): string {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '/api';
  const withoutTrailingSlash = trimmed.replace(/\/+$/, '');
  return withoutTrailingSlash.endsWith('/api') ? withoutTrailingSlash : `${withoutTrailingSlash}/api`;
}

function loadStoredApiBase(): string {
  try {
    return localStorage.getItem(API_BASE_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

export function getApiBaseInput(): string {
  const envApiBase = String(ENV_API_BASE || '').trim();
  if (envApiBase) return envApiBase;
  if (typeof import.meta !== 'undefined' && import.meta.env?.PROD) return DEFAULT_PRODUCTION_API_BASE;
  return loadStoredApiBase();
}

export function getApiBase(): string {
  return normalizeApiBase(getApiBaseInput());
}

export function setApiBase(value: string): string {
  const trimmed = value.trim();
  try {
    if (trimmed) {
      localStorage.setItem(API_BASE_STORAGE_KEY, trimmed);
    } else {
      localStorage.removeItem(API_BASE_STORAGE_KEY);
    }
  } catch {
    // noop
  }
  return getApiBase();
}

export function resolveApiUrl(path: string): string {
  return `${getApiBase()}${path}`;
}

export function getAuthToken(): string {
  try {
    return localStorage.getItem(AUTH_TOKEN_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

export function setAuthToken(value: string): string {
  const token = value.trim();
  try {
    if (token) {
      localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    }
  } catch {
    // noop
  }
  notifyAuthChange();
  return token;
}

export function clearAuthToken(): void {
  setAuthToken('');
}

export type SupabaseSessionSnapshot = {
  accessToken: string;
  refreshToken: string | null;
  expiresAt: number | null;
  userId: string | null;
  email: string | null;
};

export function hasSupabaseConfig(): boolean {
  return Boolean(String(SUPABASE_BASE_URL || '').trim() && String(SUPABASE_ANON_KEY || '').trim());
}

export function getSupabaseConfig(): { url: string; anonKey: string } | null {
  const url = String(SUPABASE_BASE_URL || '').trim();
  const anonKey = String(SUPABASE_ANON_KEY || '').trim();
  if (!url || !anonKey) return null;
  return { url, anonKey };
}

function sessionSnapshotFromSupabase(session: SupabaseSessionType | null): SupabaseSessionSnapshot | null {
  if (!session?.access_token) return null;
  return {
    accessToken: session.access_token,
    refreshToken: session.refresh_token ?? null,
    expiresAt: typeof session.expires_at === 'number' ? session.expires_at : null,
    userId: session.user?.id ?? null,
    email: typeof session.user?.email === 'string' ? session.user.email : null,
  };
}

export async function getSupabaseClient(): Promise<SupabaseClientType | null> {
  const config = getSupabaseConfig();
  if (!config) return null;
  if (!supabaseClientPromise) {
    supabaseClientPromise = import('@supabase/supabase-js').then(({ createClient }) => {
      const client = createClient(config.url, config.anonKey, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true,
        },
      });
      if (!supabaseAuthListenerReady) {
        supabaseAuthListenerReady = true;
        client.auth.onAuthStateChange((_event, session) => {
          const snapshot = sessionSnapshotFromSupabase(session);
          if (snapshot) {
            saveSupabaseSession(snapshot);
          } else {
            clearSupabaseSession();
          }
        });
      }
      return client;
    });
  }
  return supabaseClientPromise;
}

export function loadSupabaseSession(): SupabaseSessionSnapshot | null {
  try {
    const raw = localStorage.getItem(SUPABASE_SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SupabaseSessionSnapshot>;
    if (!parsed || typeof parsed.accessToken !== 'string' || !parsed.accessToken.trim()) return null;
    return {
      accessToken: parsed.accessToken,
      refreshToken: typeof parsed.refreshToken === 'string' && parsed.refreshToken.trim() ? parsed.refreshToken : null,
      expiresAt: typeof parsed.expiresAt === 'number' && Number.isFinite(parsed.expiresAt) ? parsed.expiresAt : null,
      userId: typeof parsed.userId === 'string' && parsed.userId.trim() ? parsed.userId : null,
      email: typeof parsed.email === 'string' && parsed.email.trim() ? parsed.email : null,
    };
  } catch {
    return null;
  }
}

export function saveSupabaseSession(session: SupabaseSessionSnapshot | null): void {
  try {
    if (!session) {
      localStorage.removeItem(SUPABASE_SESSION_STORAGE_KEY);
      notifyAuthChange();
      return;
    }
    localStorage.setItem(SUPABASE_SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch {
    // noop
  }
  notifyAuthChange();
}

export function clearSupabaseSession(): void {
  saveSupabaseSession(null);
}

export async function signOutSupabaseSession(): Promise<void> {
  const client = await getSupabaseClient();
  if (client) await client.auth.signOut();
  clearSupabaseSession();
}

export function notifyAuthChange(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event('academic-review-auth-change'));
}

export function getActiveAuthToken(): string {
  const session = loadSupabaseSession();
  if (session?.accessToken?.trim()) return session.accessToken.trim();
  return getAuthToken();
}

export async function getActiveAuthTokenAsync(): Promise<string> {
  const session = loadSupabaseSession();
  const nowSeconds = Math.floor(Date.now() / 1000);
  if (session?.accessToken?.trim() && (!session.expiresAt || session.expiresAt - nowSeconds > 60)) {
    return session.accessToken.trim();
  }
  const client = await getSupabaseClient();
  if (client) {
    const { data, error } = await client.auth.getSession();
    if (!error && data.session?.access_token) {
      const snapshot = sessionSnapshotFromSupabase(data.session);
      if (snapshot) saveSupabaseSession(snapshot);
      return data.session.access_token;
    }
  }
  if (session?.accessToken?.trim()) return session.accessToken.trim();
  return getAuthToken();
}

async function authHeaders(): Promise<HeadersInit> {
  const token = await getActiveAuthTokenAsync();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function parseResponseBody(raw: string): unknown {
  if (!raw.trim()) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function extractErrorMessage(body: unknown, status: number): string {
  if (body && typeof body === 'object') {
    const apiError = 'error' in body ? (body as { error?: { message?: unknown } }).error : undefined;
    if (apiError && typeof apiError === 'object' && typeof apiError.message === 'string' && apiError.message.trim()) {
      return apiError.message;
    }
    if ('message' in body) {
      const message = (body as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    }
  }
  return `HTTP ${status}`;
}

export async function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await authHeaders();
  const res = await fetch(resolveApiUrl(path), {
    ...init,
    headers: { 'Content-Type': 'application/json', ...headers, ...init?.headers },
  });

  const raw = await res.text();
  const body = parseResponseBody(raw);

  if (!res.ok) {
    const fallback = raw.trim() ? raw.trim() : `HTTP ${res.status}`;
    const message = body ? extractErrorMessage(body, res.status) : fallback;
    throw new Error(message);
  }

  return body as T;
}

export async function postRun(payload: Record<string, unknown>): Promise<{ runId: string }> {
  return fetchJSON('/runs', { method: 'POST', body: JSON.stringify(payload) });
}

export async function getMe(): Promise<Record<string, unknown>> {
  return fetchJSON('/me');
}

export async function getMyCredits(limit = 20): Promise<Record<string, unknown>> {
  return fetchJSON(`/me/credits?limit=${limit}`);
}

export async function getMyPayments(limit = 20): Promise<Record<string, unknown>> {
  return fetchJSON(`/me/payments?limit=${limit}`);
}

export async function getMyRuns(limit = 20, offset = 0): Promise<Record<string, unknown>> {
  return fetchJSON(`/me/runs?limit=${limit}&offset=${offset}`);
}

export async function getMyEmailDeliveries(limit = 20): Promise<Record<string, unknown>> {
  return fetchJSON(`/me/email-deliveries?limit=${limit}`);
}

export async function getEmailSettings(): Promise<Record<string, unknown>> {
  return fetchJSON('/me/email-settings');
}

export async function updateEmailSettings(settings: Record<string, unknown>): Promise<Record<string, unknown>> {
  return fetchJSON('/me/email-settings', { method: 'PUT', body: JSON.stringify(settings) });
}

export async function createBillingCheckout(input: { credits?: number; idempotencyKey?: string }): Promise<Record<string, unknown>> {
  return fetchJSON('/billing/checkout', { method: 'POST', body: JSON.stringify(input) });
}

export async function createBillingPortal(input: { returnUrl?: string; idempotencyKey?: string } = {}): Promise<Record<string, unknown>> {
  return fetchJSON('/billing/portal', { method: 'POST', body: JSON.stringify(input) });
}

export async function getBillingSession(sessionId: string): Promise<Record<string, unknown>> {
  return fetchJSON(`/billing/session/${encodeURIComponent(sessionId)}`);
}

export async function listRuns(limit = 100): Promise<Record<string, unknown>> {
  return fetchJSON(`/runs?limit=${limit}`);
}

export async function getRun(runId: string): Promise<Record<string, unknown>> {
  return fetchJSON(`/runs/${runId}`);
}

export async function getRunLogs(runId: string, limit = 200): Promise<Record<string, unknown>> {
  return fetchJSON(`/runs/${runId}/logs?limit=${limit}`);
}

export async function getReview(runId: string): Promise<Record<string, unknown>> {
  return fetchJSON(`/runs/${runId}/review`);
}

export async function getCitations(runId: string): Promise<Record<string, unknown>> {
  return fetchJSON(`/runs/${runId}/citations`);
}

export async function getArtifacts(runId: string): Promise<Record<string, unknown>> {
  return fetchJSON(`/runs/${runId}/artifacts`);
}

export async function getRunComputeJobs(runId: string): Promise<Record<string, unknown>> {
  return fetchJSON(`/runs/${runId}/compute-jobs`);
}

export async function getUsage(runId: string): Promise<Record<string, unknown>> {
  return fetchJSON(`/runs/${runId}/usage`);
}

export async function getRunComputePerformance(runId: string, limit = 12): Promise<Record<string, unknown>> {
  return fetchJSON(`/runs/${runId}/compute-performance?limit=${limit}`);
}

export function createEventSource(runId: string): EventSource {
  return new EventSource(resolveApiUrl(`/runs/${runId}/events`));
}

export async function getManageBootstrap(): Promise<Record<string, unknown>> {
  return fetchJSON('/manage/bootstrap');
}

function manageHeaders(key: string): HeadersInit {
  const trimmed = key.trim();
  return trimmed ? { 'X-Manage-Key': trimmed } : {};
}

export async function loginManage(key: string): Promise<Record<string, unknown>> {
  return fetchJSON('/manage/login', { method: 'POST', body: JSON.stringify({ key }) });
}

export async function getManageSession(): Promise<Record<string, unknown>> {
  return fetchJSON('/manage/session');
}

export async function getManageSummary(key: string): Promise<Record<string, unknown>> {
  return fetchJSON('/manage/summary', { headers: manageHeaders(key) });
}

export async function getManageDatabase(key: string): Promise<Record<string, unknown>> {
  return fetchJSON('/manage/database', { headers: manageHeaders(key) });
}

export async function getManageFreePlanUsage(key: string): Promise<Record<string, unknown>> {
  return fetchJSON('/manage/free-plan-usage', { headers: manageHeaders(key) });
}

export async function getManageAuditLogs(key: string, limit = 20): Promise<Record<string, unknown>> {
  return fetchJSON(`/manage/audit-logs?limit=${limit}`, { headers: manageHeaders(key) });
}

export async function getManageServices(key: string): Promise<Record<string, unknown>> {
  return fetchJSON('/manage/services', { headers: manageHeaders(key) });
}

export async function getManageDeploymentPlan(key: string): Promise<Record<string, unknown>> {
  return fetchJSON('/manage/deployment-plan', { headers: manageHeaders(key) });
}

export async function getManageRuns(key: string, limit = 20, offset = 0): Promise<Record<string, unknown>> {
  return fetchJSON(`/manage/runs?limit=${limit}&offset=${offset}`, { headers: manageHeaders(key) });
}

export async function getManageUsers(
  key: string,
  input: { limit?: number; offset?: number; q?: string; role?: string; status?: string } = {}
): Promise<Record<string, unknown>> {
  const params = new URLSearchParams();
  params.set('limit', String(input.limit ?? 50));
  params.set('offset', String(input.offset ?? 0));
  if (input.q) params.set('q', input.q);
  if (input.role) params.set('role', input.role);
  if (input.status) params.set('status', input.status);
  return fetchJSON(`/manage/users?${params.toString()}`, { headers: manageHeaders(key) });
}

export async function getManageBillingSummary(key: string): Promise<Record<string, unknown>> {
  return fetchJSON('/manage/billing/summary', { headers: manageHeaders(key) });
}

export async function getManagePayments(
  key: string,
  input: { limit?: number; offset?: number; userId?: string; status?: string; provider?: string } = {}
): Promise<Record<string, unknown>> {
  const params = new URLSearchParams();
  params.set('limit', String(input.limit ?? 50));
  params.set('offset', String(input.offset ?? 0));
  if (input.userId) params.set('userId', input.userId);
  if (input.status) params.set('status', input.status);
  if (input.provider) params.set('provider', input.provider);
  return fetchJSON(`/manage/billing/payments?${params.toString()}`, { headers: manageHeaders(key) });
}

export async function refundManagePayment(
  key: string,
  sessionId: string,
  input: { amountCents?: number; reason?: string; idempotencyKey?: string } = {}
): Promise<Record<string, unknown>> {
  return fetchJSON(`/manage/billing/payments/${encodeURIComponent(sessionId)}/refund`, {
    method: 'POST',
    headers: manageHeaders(key),
    body: JSON.stringify(input),
  });
}

export async function getManageLedger(
  key: string,
  input: { limit?: number; offset?: number; userId?: string; type?: string } = {}
): Promise<Record<string, unknown>> {
  const params = new URLSearchParams();
  params.set('limit', String(input.limit ?? 50));
  params.set('offset', String(input.offset ?? 0));
  if (input.userId) params.set('userId', input.userId);
  if (input.type) params.set('type', input.type);
  return fetchJSON(`/manage/billing/ledger?${params.toString()}`, { headers: manageHeaders(key) });
}

export async function getManageEmailDeliveries(
  key: string,
  input: { limit?: number; offset?: number; userId?: string; status?: string; provider?: string } = {}
): Promise<Record<string, unknown>> {
  const params = new URLSearchParams();
  params.set('limit', String(input.limit ?? 50));
  params.set('offset', String(input.offset ?? 0));
  if (input.userId) params.set('userId', input.userId);
  if (input.status) params.set('status', input.status);
  if (input.provider) params.set('provider', input.provider);
  return fetchJSON(`/manage/emails/deliveries?${params.toString()}`, { headers: manageHeaders(key) });
}

export async function sendManageTestEmail(
  key: string,
  input: { toEmail: string; subject?: string; text?: string; idempotencyKey?: string }
): Promise<Record<string, unknown>> {
  return fetchJSON('/manage/emails/test', {
    method: 'POST',
    headers: {
      ...manageHeaders(key),
      ...(input.idempotencyKey ? { 'Idempotency-Key': input.idempotencyKey } : {}),
    },
    body: JSON.stringify(input),
  });
}

export async function getManageUser(key: string, userId: string): Promise<Record<string, unknown>> {
  return fetchJSON(`/manage/users/${encodeURIComponent(userId)}`, { headers: manageHeaders(key) });
}

export async function updateManageUser(key: string, userId: string, patch: Record<string, unknown>): Promise<Record<string, unknown>> {
  return fetchJSON(`/manage/users/${encodeURIComponent(userId)}`, {
    method: 'PATCH',
    headers: manageHeaders(key),
    body: JSON.stringify(patch),
  });
}

export async function adjustManageUserCredits(
  key: string,
  userId: string,
  input: { amountCredits: number; reason: string; idempotencyKey?: string }
): Promise<Record<string, unknown>> {
  return fetchJSON(`/manage/users/${encodeURIComponent(userId)}/credits/adjust`, {
    method: 'POST',
    headers: manageHeaders(key),
    body: JSON.stringify(input),
  });
}

export async function getManageRunDetail(key: string, runId: string): Promise<Record<string, unknown>> {
  return fetchJSON(`/manage/runs/${runId}`, { headers: manageHeaders(key) });
}

export async function manageActionRun(key: string, runId: string, action: 'cancel' | 'resume'): Promise<Record<string, unknown>> {
  return fetchJSON(`/manage/runs/${runId}/${action}`, { method: 'POST', headers: manageHeaders(key) });
}

export async function getManageConfig(key: string): Promise<Record<string, unknown>> {
  return fetchJSON('/manage/config', { headers: manageHeaders(key) });
}

export async function updateManageConfig(key: string, config: Record<string, unknown>): Promise<Record<string, unknown>> {
  return fetchJSON('/manage/config', { method: 'PUT', headers: manageHeaders(key), body: JSON.stringify({ config }) });
}

export async function getManageProviderUsage(key: string): Promise<Record<string, unknown>> {
  return fetchJSON('/manage/providers/usage', { headers: manageHeaders(key) });
}

export async function updateManageProvider(
  key: string,
  providerId: string,
  patch: Record<string, unknown>
): Promise<Record<string, unknown>> {
  return fetchJSON(`/manage/providers/${encodeURIComponent(providerId)}`, {
    method: 'PATCH',
    headers: manageHeaders(key),
    body: JSON.stringify(patch),
  });
}

export async function deleteManageProvider(key: string, providerId: string): Promise<Record<string, unknown>> {
  return fetchJSON(`/manage/providers/${encodeURIComponent(providerId)}`, {
    method: 'DELETE',
    headers: manageHeaders(key),
  });
}

export async function getManageSourceUsage(key: string): Promise<Record<string, unknown>> {
  return fetchJSON('/manage/sources/usage', { headers: manageHeaders(key) });
}

export async function updateManageSource(
  key: string,
  sourceName: string,
  patch: Record<string, unknown>
): Promise<Record<string, unknown>> {
  return fetchJSON(`/manage/sources/${encodeURIComponent(sourceName)}`, {
    method: 'PATCH',
    headers: manageHeaders(key),
    body: JSON.stringify(patch),
  });
}

const MODEL_TEST_TARGETS = new Set(['llm', 'embedding', 'rerank']);
const SOURCE_TEST_TARGETS = new Set(['openalex', 'crossref', 'semantic_scholar', 'pubmed', 'arxiv', 'biorxiv', 'medrxiv']);

export async function testManageTarget(key: string, target: string, name?: string, config?: Record<string, unknown>): Promise<Record<string, unknown>> {
  if (MODEL_TEST_TARGETS.has(target)) {
    const providerId = typeof config?.providerId === 'string' ? config.providerId : undefined;
    const rerankProbe = target === 'rerank'
      ? {
        query: String(config?.query || 'Klebsiella pneumoniae antibiotic resistance mechanisms'),
        documents: Array.isArray(config?.documents) ? config.documents : [
          'Klebsiella pneumoniae resistance mechanisms include carbapenemases, porin loss, and efflux regulation.',
          'Antibiotic stewardship and susceptibility testing improve clinical outcomes for resistant infections.',
          'This document is about galaxy morphology and is unrelated to bacterial antimicrobial response.',
        ],
        topN: Number(config?.topN || config?.top_n || 3),
      }
      : {};
    return fetchJSON('/manage/providers/test', {
      method: 'POST',
      headers: manageHeaders(key),
      body: JSON.stringify({ taskType: target, name, providerId, config, ...rerankProbe }),
    });
  }

  if (SOURCE_TEST_TARGETS.has(target)) {
    return fetchJSON('/manage/sources/test', {
      method: 'POST',
      headers: manageHeaders(key),
      body: JSON.stringify({
        source: target,
        query: String(config?.query || name || 'academic review'),
        maxResults: Number(config?.maxResults || config?.max_results || 10),
      }),
    });
  }

  return fetchJSON('/manage/test', {
    method: 'POST',
    headers: manageHeaders(key),
    body: JSON.stringify({ target, name, config }),
  });
}

export async function rotateManageKey(key: string): Promise<Record<string, unknown>> {
  return fetchJSON('/manage/key/rotate', { method: 'POST', headers: manageHeaders(key) });
}
